import { Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators,
  FormControl,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { InputFieldComponent } from '../../../../components/ui/inputField/inputField.component';
import { MessageTemplate } from '../../../../services/message-templates/message.template';
import { FeedbackCardComponent } from '../../../../components/toasts-and-cards/feedback-card/feedback-card.component';
import { MessageAttributeMenuComponent } from '../../../../components/ui/message-attribute-menu/message-attribute-menu.component';
import { MessageAttributeChipsComponent } from '../../../../components/ui/message-attribute-chips/message-attribute-chips.component';
import { SmsBubbleComponent } from '../../../../components/messages/sms-bubble/sms-bubble.component';
import { SmsMeterComponent } from '../create-new-message/partials/sms-meter/sms-meter.component';
import { removeMessageAttribute } from '../../../../../utils/utils';

/** Text that is only spaces counts as empty. */
function notBlank(control: AbstractControl): ValidationErrors | null {
  return String(control.value ?? '').trim() ? null : { required: true };
}

const CATEGORIES = ['Promotional', 'Educational', 'Informational', 'Transactional'];

@Component({
  selector: 'message-templates',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputFieldComponent,
    FeedbackCardComponent,
    MessageAttributeMenuComponent,
    MessageAttributeChipsComponent,
    SmsBubbleComponent,
    SmsMeterComponent,
  ],
  templateUrl: './message-templates-component.html',
  styleUrls: ['./message-template-style.css'],
})
export class MessageTemplates implements OnInit {
  private destroyRef = inject(DestroyRef);

  templateForm!: FormGroup;
  textareaControl = new FormControl('', { validators: [notBlank] });
  template_name: string = '';
  category: string = '';
  content: string = '';
  loading: boolean = false;
  /** The template being edited is still loading. */
  loadingTemplate: boolean = false;
  submitted: boolean = false;
  fetchedTemplate: any = null;
  templateId: string | number = '';
  feedbackMessage: string = '';
  feedbackType: 'success' | 'error' | 'info' | 'warning' = 'success';
  private host: ElementRef<HTMLElement> = inject(ElementRef);

  @ViewChild('bodyInput') private bodyInput?: ElementRef<HTMLTextAreaElement>;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private messageTemplate: MessageTemplate,
    private route: ActivatedRoute
  ) {}

  /** The fixed categories, plus the saved one if it is not among them. */
  get categories(): string[] {
    const saved = this.fetchedTemplate?.category;
    return saved && !CATEGORIES.includes(saved) ? [...CATEGORIES, saved] : CATEGORIES;
  }

  /**
   * Off only while saving, or while an opened template is unchanged. An
   * incomplete form stays submittable so its errors can say what is missing.
   */
  get saveDisabled(): boolean {
    return (
      this.loading ||
      Boolean(this.fetchedTemplate && this.templateForm.pristine && this.textareaControl.pristine)
    );
  }

  fetchTemplateDetails(id: string): void {
    this.loadingTemplate = true;
    this.messageTemplate.fetchTemplateDetail(id).subscribe({
      next: (details) => {
        this.loadingTemplate = false;
        if (details) {
          this.fetchedTemplate = details;
          this.templateForm.patchValue({
            templateName: details.name || '',
            category: details.category || '',
          });
          this.content = details.content || '';
          this.textareaControl.setValue(this.content);
          this.templateForm.markAsPristine();
          this.textareaControl.markAsPristine();
        }
      },
      error: () => {
        this.loadingTemplate = false;
        this.showFeedback('This template did not load. Go back to Templates and open it again.', 'error');
      },
    });
  }

  ngOnInit() {
    this.templateForm = this.fb.group({
      templateName: ['', Validators.required],
      category: ['', Validators.required],
    });

    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.templateId = id;
        this.fetchTemplateDetails(id);
      }
    });

    // Keep the preview fields in step with the form
    this.templateForm
      .get('templateName')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.template_name = value;
      });

    this.templateForm
      .get('category')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.category = value;
      });

    this.textareaControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.content = value || '';
    });

    this.messageTemplate.loading.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((loading) => {
      this.loading = loading;
    });

  }

  onSubmit(): void {
    this.submitted = true;
    // A new attempt replaces the last notice (and lets the same error show again).
    this.feedbackMessage = '';
    if (this.templateForm.invalid || this.textareaControl.invalid) {
      this.focusFirstProblem();
      return;
    }
    if (this.templateForm.valid && this.textareaControl.valid) {
      const templateData = this.templateForm.value;
      const date = new Date();

      const persist_data: any = {
        name: templateData.templateName,
        content: this.content,
        category: templateData.category,
        createdAt: date,
        templateType: 'Em',
        contentType: 'Em',
        requestTypeId: null,
        communicationsCount: null,
        permittedForUser: null,
        unlayerTemplate: null,
        uuid: null,
        organisationId: null,
        preview: null,
        createdBy: null,
      };

      // If templateId exists, edit the template, otherwise create a new one
      if (this.templateId) {
        // Preserve identifiers/metadata required by BE update validation and entity mapping.
        persist_data.id = Number(this.templateId);
        persist_data.uuid = this.fetchedTemplate?.uuid ?? null;
        persist_data.organisationId = this.fetchedTemplate?.organisationId ?? null;
        persist_data.createdBy = this.fetchedTemplate?.createdBy ?? null;
        persist_data.createdAt = this.fetchedTemplate?.createdAt ?? date;

        this.messageTemplate.editTemplate(this.templateId, persist_data).subscribe({
          next: () => {
            this.showFeedback('Template saved.', 'success');
            this.router.navigate(['/organization/messages'], { queryParams: { tab: 'templates' } });
          },
          error: (err) => {
            this.showFeedback(
              this.getApiErrorMessage(err, 'The template was not saved. Check your connection and try again.'),
              'error'
            );
          },
        });
      } else {
        this.messageTemplate.createMessage(persist_data).subscribe({
          next: () => {
            this.showFeedback('Template created.', 'success');
            this.router.navigate(['/organization/messages'], { queryParams: { tab: 'templates' } });
          },
          error: (err) => {
            this.showFeedback(
              this.getApiErrorMessage(err, 'The template was not created. Check your connection and try again.'),
              'error'
            );
          },
        });
      }
    }
  }

  handleBack() {
    this.router.navigate(['organization', 'messages'], { queryParams: { tab: 'templates' } });
  }

  /** Insert a {{field}} placeholder at the cursor (double braces, as compose expects). */
  appendAttribute(name: string) {
    const placeholder = `{{${name}}}`;
    const current = this.textareaControl.value || '';
    const textarea = this.bodyInput?.nativeElement;
    const start = textarea ? textarea.selectionStart : current.length;
    const end = textarea ? textarea.selectionEnd : current.length;
    this.textareaControl.setValue(current.substring(0, start) + placeholder + current.substring(end));
    this.textareaControl.markAsDirty();

    if (textarea) {
      const position = start + placeholder.length;
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(position, position);
      });
    }
  }

  removeAttribute(attribute: string) {
    this.textareaControl.setValue(removeMessageAttribute(this.textareaControl.value || '', attribute));
    this.textareaControl.markAsDirty();
  }

  /**
   * feedback-card times the notice itself (and pauses on hover or focus), so
   * nothing is cleared here: an error stays until it is dismissed or the next
   * save replaces it.
   */
  private showFeedback(
    message: string,
    type: 'success' | 'error' | 'info' | 'warning'
  ): void {
    this.feedbackMessage = message;
    this.feedbackType = type;
  }

  /** Bring the first field with an error into view and focus its control. */
  private focusFirstProblem() {
    if (typeof window === 'undefined') return;
    setTimeout(() => {
      const field = this.host.nativeElement.querySelector<HTMLElement>('.bk-field.is-invalid');
      if (!field) return;
      field.scrollIntoView({ block: 'center' });
      field.querySelector<HTMLElement>('input, select, textarea')?.focus({ preventScroll: true });
    });
  }

  private getApiErrorMessage(err: any, fallback: string): string {
    const apiMessage =
      err?.error?.message ||
      err?.error?.detail ||
      err?.message;
    // Angular's own "Http failure response for …" text means nothing to an operator.
    return typeof apiMessage === 'string' &&
      apiMessage.trim().length > 0 &&
      !/^Http failure/i.test(apiMessage)
      ? apiMessage
      : fallback;
  }
}
